import React, { useEffect, useRef, useState } from 'react'
import Swiper from 'swiper'

import { get } from '@js/common/api'

import CTA from '@f/elements/CTA'
import Icon from '@f/elements/Icon'

import ListPagination from './ListPagination'
import MapFilters from './MapFilters'
import MapFiltersBar from './MapFiltersBar'
import OfficeDetail from './OfficeDetail'
import OfficeDetailCard from './OfficeDetailCard'
import { getUrl } from '@js/common/getUrlByDomain'

const MobileView = ({
  actions: {
    setActiveID,
    setActiveIDonMap,
    setMapClicked,
    applyQuery,
    updateQuery,
    setLocationParams,
    setDisplayingFilters
  },
  data: {
    apiResponse: { results = [], count = 0 },
    params,
    locationParams,
    activeID,
    activeIDonMap,
    mapClicked,
    displayingFilters
  },
  ...props
}) => {
  const [displayingContent, setDisplayingContent] = useState(false)
  const [displayingCards, setDisplayingCards] = useState(false)
  const [selectedOffice, setSelectedOffice] = useState(null)

  const handleClick = ({ event, office }) => {
    event.preventDefault()
    setDisplayingContent(false)

    setTimeout(() => {
      setSelectedOffice(office.nid)
    }, 250)
  }

  const handleCardClick = ({ event, office }) => {
    event.preventDefault()
    setSelectedOffice(office.nid)
  }

  useEffect(() => {
    setActiveID(activeIDonMap)
  }, [activeIDonMap])

  useEffect(() => {
    if (activeID) {
      if (!displayingCards) {
        setDisplayingCards(true)
      } else if (SwiperObject.current) {
        SwiperObject.current.slideTo(
          results.findIndex(result => result.nid === activeID) || 0
        )
      }
    }
  }, [activeID])

  useEffect(() => {
    if (mapClicked && displayingCards) {
      setActiveID(null)
      setActiveIDonMap(null)
      setDisplayingCards(false)
      setMapClicked(false)
    }
  }, [mapClicked])

  useEffect(() => {
    if (displayingCards) {
      setMapClicked(false)
    }
  }, [displayingCards])

  const [officeDetails, setOfficeDetails] = useState({})

  useEffect(() => {
    if (selectedOffice) {
      const officeInfo = results.find(office => office.nid === selectedOffice)

      get('vendors', {
        nid__in: officeInfo.vendor_nid
      }).then(data => {
        setOfficeDetails({
          office: officeInfo,
          vendor: data.results[0]
        })
      })
    } else {
      setOfficeDetails({})
    }
  }, [selectedOffice])

  useEffect(() => {
    if (officeDetails.office) {
      setDisplayingContent(true)
    }
  }, [officeDetails])

  const mobileContentWrap = useRef(null)

  useEffect(() => {
    if (params.offset) {
      setSelectedOffice(null)
      setDisplayingCards(false)
      mobileContentWrap.current.scrollTop = 0
    } else {
      setSelectedOffice(null)
      setDisplayingCards(false)
      setDisplayingContent(false)
    }
  }, [results])

  const propsForPartials = {
    actions: { updateQuery },
    data: {
      apiResponse: { results, count },
      params
    }
  }

  const mobileCardsWrap = useRef(null)

  const SwiperObject = useRef(null)
  useEffect(() => {
    if (displayingCards) {
      SwiperObject.current = new Swiper(mobileCardsWrap.current, {
        containerModifierClass: 'MapView-cards-inner-',
        wrapperClass: 'MapView-cards-inner',
        slideClass: 'OfficeMap-list-item',
        spaceBetween: 8,
        slidersPerView: 1,
        initialSlide: results.findIndex(result => result.nid === activeID) || 0
      })

      SwiperObject.current.on('slideChange', () => {
        if (SwiperObject.current.activeIndex < 20) {
          setActiveID(results[SwiperObject.current.activeIndex].nid)
        }
      })
    } else {
      if (SwiperObject.current) SwiperObject.current.destroy(true, true)
    }
  }, [displayingCards])

  const displayPaginationInCards =
    (params.offset ? parseInt(params.offset) : 1) * 20 < count

  const activeFilters = Object.keys(params).filter(
    param => !/lat__range|lon__range|offset/.test(param)
  ).length

  return (
    <>
      <div className='[ f-pos-ab ] MapView-filters-wrap'>
        <CTA
          type='block-secondary'
          tag='button'
          className='[  f-pos-ab f-p-v-010 f-p-h-020 f-interactive ] is-inverted MapView-map-cta MapView-filters-wrap-toggle'
          onClick={() => setDisplayingFilters(display => !display)}>
          <Icon
            icon='filter-16px'
            className='[ f-icon-020 f-c-st-inherit f-icon-t-mini f-m-r-010 ]'
          />
          Filter{activeFilters > 0 ? ` • ${activeFilters}` : ''}
        </CTA>

        <div
          className={`[ f-c-ba-whi f-p-a-020 f-p-t-080 f-interactive ] MobileView-filters-wrap ${
            displayingFilters ? 'is-shown' : ''
          } ${props.panelDisplayed?.filter ? 'is-displaying-filter' : ''}`}>
          <CTA
            type='icon'
            tag='button'
            onClick={() => setDisplayingFilters(display => !display)}
            className='[ f-pos-ab f-p-a-005 ] MapView-filters-close-icon'>
            <Icon icon='close-16px' className='[ f-icon-020 f-c-st-inherit ]' />
          </CTA>
          <MapFiltersBar
            params={params}
            displayPanel={props.displayPanel}
            availableFilters={props.availableFilters}
            panelDisplayed={props.panelDisplayed}
            locationParams={locationParams}
            setLocationParams={setLocationParams}
          />
          <MapFilters
            params={params}
            displayPanel={props.displayPanel}
            availableFilters={props.availableFilters}
            panelDisplayed={props.panelDisplayed}
            removeFilter={props.removeFilter}
            updateQuery={updateQuery}
            apiResult={results}
            locationParams={locationParams}
            setLocationParams={setLocationParams}
            applyQuery={applyQuery}
          />
        </div>
      </div>

      <CTA
        type='icon'
        tag='button'
        onClick={() => {
          setDisplayingContent(false)
          setSelectedOffice(null)
        }}
        className={`[ f-pos-ab f-p-a-010 f-c-ba-whi f-shadow-action f-interactive ] is-inverted MapView-top-right-CTA ${
          displayingContent ? 'is-shown' : ''
        }`}>
        <Icon icon='close-16px' className='[ f-icon-020 f-c-st-inherit ]' />
        <span className='[ f-visually-hidden ]'>Close</span>
      </CTA>
      <CTA
        type='block-secondary'
        tag='a'
        href={`${getUrl('vendorList')()}`}
        className={`[ f-pos-ab f-p-v-010 f-p-h-020 f-interactive ] is-inverted MapView-top-right-CTA MapView-map-cta ${
          displayingContent ? '' : 'is-shown'
        }`}
        style={{ zIndex: 0 }}>
        Anbieterliste
        <Icon
          icon='grid-list-16px'
          className='[ f-icon-020 f-c-st-inherit f-icon-t-mini f-m-l-010 ]'
        />
      </CTA>

      <div
        className={`[ f-pos-ab f-overflow f-p-h-020 f-interactive ] MapView-cards-wrap ${
          displayingCards ? 'is-shown' : ''
        }`}
        ref={mobileCardsWrap}>
        <div className='[ f-d-flr f-interactive ] MapView-cards-inner'>
          {results.map(office => {
            return (
              <button
                key={office.nid}
                id={`office_${office.nid}`}
                className={`[ f-pos-rel f-d-flr f-fl-wrap f-radius-card-medium f-shadow-action f-m-b-020 f-p-a-0 f-c-ba-whi f-t-left ] OfficeMap-list-item is-mobile-card`}
                onClick={event =>
                  handleCardClick({
                    event,
                    office
                  })
                }>
                <OfficeDetailCard type='list' item={office} />
              </button>
            )
          })}
          {displayPaginationInCards ? (
            <div
              id={`pagination_card`}
              className='[ f-pos-rel f-d-flr f-fl-ac-ce f-fl-jc-ce f-fl-wrap f-radius-card-large f-shadow-action f-m-l-010
                 f-m-b-020 f-p-a-0 f-c-ba-whi f-t-left ] OfficeMap-list-item is-mobile-card'>
              <CTA
                type='block-secondary'
                tag='button'
                className='[ f-m-a-auto f-m-v-050 ]'
                onClick={() =>
                  updateQuery({
                    offset: params.offset ? parseInt(params.offset) + 1 : 2
                  })
                }>
                Weitere Anbieter anzeigen
              </CTA>
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={`[ f-pos-ab f-c-ba-whi f-interactive ] MapView-content-wrap ${
          displayingContent ? 'is-shown' : ''
        }`}
        ref={mobileContentWrap}>
        {officeDetails.office ? (
          <OfficeDetail
            officeDetails={officeDetails}
            setSelectedOffice={setSelectedOffice}
          />
        ) : (
          <>
            <div className='[ f-p-h-020 f-p-b-020 ]'>
              {results.map(office => {
                return (
                  <button
                    key={office.nid}
                    id={`office_${office.nid}`}
                    className='[ f-pos-rel f-radius-card-medium f-shadow-strong f-m-t-020 f-p-a-0 ] OfficeMap-list-item'
                    onClick={event =>
                      handleClick({
                        event,
                        office
                      })
                    }>
                    <OfficeDetailCard type='list' item={office} />
                  </button>
                )
              })}
            </div>
            <ListPagination {...propsForPartials} />
          </>
        )}
      </div>
      {displayingContent || displayingCards ? null : (
        <CTA
          type='block-secondary'
          tag='button'
          onClick={() => setDisplayingContent(true)}
          className='[ f-pos-ab f-p-v-010 f-p-h-020 f-interactive ] MapView-bottom-center-CTA MapView-map-cta'>
          Anbieter anzeigen
        </CTA>
      )}
    </>
  )
}

export default MobileView
